import { Menu, MenuItem, Sidebar, SubMenu, useProSidebar } from 'react-pro-sidebar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { Link, useLocation } from 'react-router-dom';
import React, { useMemo } from 'react';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MemoryIcon from '@mui/icons-material/Memory';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import PaymentIcon from '@mui/icons-material/Payment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import { menuItemStyles, themes } from './constants/constants';
import { hexToRgba } from './utils/utils';
import useRole from '../../hooks/use-role';
import { CloudDownload, Sensors } from '@mui/icons-material';
import useSupportAccess from '../support/use-support-access';
import { useSupportStore } from '../support/state/use-support-store';
import useVisibleInterval from '../support/use-visible-interval';
import { SUMMARY_POLL_MS } from '../support/constants';
import UnreadBadge from '../support/components/UnreadBadge';

const CustomSideBar = () => {
  const [theme] = React.useState('light');
  const { isEngineer, isMeridianOrg } = useRole();
  const { canRead: canReadSupport } = useSupportAccess();
  const { summary: { unread_messages: unreadMessages, new: newThreads }, fetchSummary } = useSupportStore();
  const badgeCount = unreadMessages > 0 ? unreadMessages : newThreads || 0;
  const { collapseSidebar, toggleSidebar, broken } = useProSidebar();
  const { pathname } = useLocation();

  useVisibleInterval(fetchSummary, SUMMARY_POLL_MS, canReadSupport, true);

  // menu sidebar:
  const menuItems = useMemo(() => {
    return [
      { label: 'Сводка', to: '/', icon: <SpeedIcon /> },
      { label: 'Пользователи', to: '/users', icon: <GroupIcon /> },
      ...(canReadSupport ? [{ label: 'Поддержка', to: '/support', icon: <SupportAgentIcon />, badge: badgeCount }] : []),
      ...(!isEngineer ? [{ label: 'Инженеры', to: '/engineers', icon: <EngineeringIcon /> }] : []),
      { label: 'События', to: '/events', icon: <EventNoteIcon /> },
      ...(isMeridianOrg
        ? [
            { label: 'Журнал OTA', to: '/ota-events', icon: <CloudDownload /> },
            { label: 'Метрон', to: '/metron-devices', icon: <Sensors /> },
          ]
        : []),
      { label: 'Адреса', to: '/addresses', icon: <ApartmentIcon /> },
      { label: 'Устройства', to: '/controllers', icon: <MemoryIcon /> },
      { label: 'Интеграции', to: '/integrations', icon: <SettingsEthernetIcon /> },
      // { label: 'Архив интеграций', to: '/archiveIntegrations', icon: <ArchiveIcon /> },
      { label: 'Отчеты', to: '/reports', icon: <AssessmentIcon /> },
      { label: 'Риски', to: '/risks', icon: <ReportProblemIcon /> },
      ...(!isEngineer ? [{ label: 'Платежи', to: '/payments', icon: <PaymentIcon /> }] : []),
      {
        label: 'Настройки',
        icon: <SettingsIcon />,
        subMenu: [
          { label: 'Быстрая регистрация', to: '/quick-registration' },
          ...(!isEngineer
            ? [
                { label: 'Добавить инженера', to: '/invitation' },
                { label: 'Роли и разрешения', to: '/roles-and-permissions' },
                { label: 'Обновления прошивок', to: '/devices-update' },
                ...(isMeridianOrg ? [{ label: 'Обкатка прошивок', to: '/firmware-test' }] : []),
              ]
            : []),
        ],
      },
      { label: 'Личный кабинет', to: '/user', icon: <AccountCircleRoundedIcon /> },
    ];
  }, [isEngineer, isMeridianOrg, canReadSupport, badgeCount]);

  return (
    <div className="flex select-none sticky top-0 left-0 h-screen z-40">
      <Sidebar
        rootStyles={{
          color: themes[theme].sidebar.color,
          borderWidth: 0,
        }}
        backgroundColor={hexToRgba(themes[theme].sidebar.backgroundColor, 1)}
        width="280px"
        breakPoint="md">
        <Menu menuItemStyles={menuItemStyles(theme)} renderExpandIcon={({ open }) => (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}>
          <MenuItem
            onClick={() => {
              collapseSidebar();
            }}
            className="mb-10"
            icon={<MenuRoundedIcon />}>
            <p className="font-bold">Мира Бизнес</p>
          </MenuItem>

          {menuItems?.map((item) => {
            const { label, to, icon, subMenu, badge } = item;
            if (subMenu?.length) {
              return (
                <SubMenu key={label} label={label} icon={icon}>
                  {subMenu.map((subItem) => (
                    <MenuItem key={subItem.to} active={pathname === subItem.to} component={<Link to={subItem.to} />}>
                      {subItem.label}
                    </MenuItem>
                  ))}
                </SubMenu>
              );
            }
            const isActive = pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));
            return (
              <MenuItem active={isActive} onClick={() => broken && toggleSidebar()} key={to} component={<Link to={to} className="link" />} icon={icon}>
                <span className="inline-flex items-center gap-2">
                  {label}
                  <UnreadBadge count={badge} />
                </span>
              </MenuItem>
            );
          })}
        </Menu>
      </Sidebar>
    </div>
  );
};
export default CustomSideBar;
